// src/services/exportService.js
import { api } from './api';

/**
 * Export service for handling data exports across the application
 * Provides consistent interfaces for different export formats and analytics types
 */

/**
 * Export analytics data in specified format
 * @param {Object} params - Export parameters
 * @param {string} params.startDate - Start date in YYYY-MM-DD format
 * @param {string} params.endDate - End date in YYYY-MM-DD format
 * @param {string} params.format - Export format (excel, csv, pdf)
 * @param {string} params.type - Analytics type (dashboard, campaign, content, social, client, brand)
 * @param {string} params.id - Optional ID for specific entity (campaign, client, etc.)
 * @returns {Promise<Blob>} Exported data as blob for download
 */
export const exportAnalyticsData = async (params) => {
  try {
    const response = await api.get('/analytics/export', {
      params,
      responseType: 'blob',
    });
    
    return response.data;
  } catch (error) {
    console.error('Export analytics error:', error);
    throw error;
  }
};

/**
 * Generate appropriate filename for downloaded export
 * @param {Object} params - Export parameters
 * @returns {string} Formatted filename
 */
export const generateExportFilename = (params) => {
  const { type, format, id, name } = params;
  const dateStr = new Date().toISOString().split('T')[0];
  const entityName = name ? `-${name.replace(/\s+/g, '-').toLowerCase()}` : id ? `-${id}` : '';
  
  return `${type}-analytics${entityName}-${dateStr}.${getExtension(format)}`;
};

/**
 * Get file extension for export format
 * @param {string} format - Export format
 * @returns {string} File extension
 */
const getExtension = (format) => {
  switch (format?.toLowerCase()) {
    case 'excel':
      return 'xlsx';
    case 'csv':
      return 'csv';
    case 'pdf':
      return 'pdf';
    case 'json':
      return 'json';
    default:
      return 'xlsx';
  }
};

/**
 * Create and trigger download for exported data
 * @param {Blob} data - Data blob to download
 * @param {string} filename - Filename for download
 */
export const downloadExport = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Convenience function to export and download in one call
 * @param {Object} params - Export parameters
 * @returns {Promise<boolean>} Success indicator
 */
export const exportAndDownload = async (params) => {
  try {
    const data = await exportAnalyticsData(params);
    const filename = generateExportFilename(params);
    downloadExport(data, filename);
    return true;
  } catch (error) {
    console.error('Export and download error:', error);
    throw error;
  }
};

/**
 * Export dashboard analytics
 * @param {Object} params - Export parameters
 * @returns {Promise<boolean>} Success indicator
 */
export const exportDashboardAnalytics = async (params) => {
  return exportAndDownload({
    ...params,
    type: 'dashboard',
  });
};

/**
 * Export campaign analytics
 * @param {Object} params - Export parameters
 * @param {string} params.campaignId - Optional campaign ID
 * @returns {Promise<boolean>} Success indicator
 */
export const exportCampaignAnalytics = async (params) => {
  return exportAndDownload({
    ...params,
    type: 'campaign',
    id: params.campaignId,
  });
};

/**
 * Export content analytics
 * @param {Object} params - Export parameters
 * @returns {Promise<boolean>} Success indicator
 */
export const exportContentAnalytics = async (params) => {
  return exportAndDownload({
    ...params,
    type: 'content',
  });
};

/**
 * Export social media analytics
 * @param {Object} params - Export parameters
 * @returns {Promise<boolean>} Success indicator
 */
export const exportSocialMediaAnalytics = async (params) => {
  return exportAndDownload({
    ...params,
    type: 'social',
  });
};

/**
 * Export client analytics
 * @param {Object} params - Export parameters
 * @param {string} params.clientId - Client ID
 * @param {string} params.clientName - Client name for filename
 * @returns {Promise<boolean>} Success indicator
 */
export const exportClientAnalytics = async (params) => {
  return exportAndDownload({
    ...params,
    type: 'client',
    id: params.clientId,
    name: params.clientName,
  });
};

/**
 * Export brand analytics
 * @param {Object} params - Export parameters
 * @param {string} params.brandId - Brand ID
 * @param {string} params.brandName - Brand name for filename
 * @returns {Promise<boolean>} Success indicator
 */
export const exportBrandAnalytics = async (params) => {
  return exportAndDownload({
    ...params,
    type: 'brand',
    id: params.brandId,
    name: params.brandName,
  });
};

/**
 * Export custom report
 * @param {Object} params - Export parameters
 * @param {Object} params.reportConfig - Report configuration
 * @returns {Promise<boolean>} Success indicator
 */
export const exportCustomReport = async (params) => {
  return exportAndDownload({
    ...params,
    type: 'custom-report',
    name: params.reportConfig?.name || 'custom-report',
  });
};